import type { HTMLAttributes } from 'react';
import { OwnCarrierAccountMark } from '@components/OwnCarrierAccountMark';
import type { ProviderRatePackage } from '@features/order-details';
import { DangerText, MuteText } from '@elements/Common';
import { DarkPopup } from '@elements/Popup';
import { CurrencyDollar, Warehouse, WarningIcon } from '@phosphor-icons/react';
import { Badge, cn, Spinner } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { carrierServices } from '@utility/constants/common';

const MINI_PILL = 'sk:px-1.5 sk:py-0.5 sk:text-xs';

export function DangerSmallBadge({
  className,
  children,
}: HTMLAttributes<HTMLSpanElement>) {
  return (
    <Badge tone="danger" className={cn(MINI_PILL, className)}>
      {children}
    </Badge>
  );
}

export function FPFetchRateErrorTrigger({ message }: { message: string }) {
  return (
    <DarkPopup
      trigger={
        <div className="sk:flex sk:cursor-pointer sk:items-center sk:gap-1">
          <DangerText>Error</DangerText>
          <WarningIcon className="sk:text-danger" />
        </div>
      }
    >
      {message}
    </DarkPopup>
  );
}

export function FPOptionRender({ item, providerRates, minRate }) {
  return (
    <div className="sk:flex sk:items-center sk:justify-between sk:gap-2">
      <div className="sk:mb-1 sk:flex sk:items-center sk:gap-2">
        <Warehouse className="sk:shrink-0 sk:text-fg-tertiary" />
        <MuteText className="sk:font-normal">{item.text}</MuteText>
        {providerRates[item.value]?.rate &&
          providerRates[item.value]?.rate === minRate && (
            <Badge tone="success" className={MINI_PILL}>
              <CurrencyDollar />
              Best Price
            </Badge>
          )}
        {!!item?.item?.showOutOfStockBadge && (
          <DangerSmallBadge>OOS</DangerSmallBadge>
        )}
      </div>
      {providerRates[item.value]?.loading ? (
        <Spinner size="sm" />
      ) : providerRates[item.value]?.rate ? (
        <span>
          {CommonUtility.currencyFormat(providerRates[item.value]?.rate)}
        </span>
      ) : providerRates[item.value]?.fetchRateError ? (
        <FPFetchRateErrorTrigger
          message={providerRates[item.value].fetchRateError}
        />
      ) : (
        ''
      )}
    </div>
  );
}

export function FPOptionOrganizationRender({ providerKey }) {
  return (
    <div className="sk:mb-2 sk:flex sk:items-center sk:gap-2 sk:text-sm sk:font-normal">
      <div className="sk:flex sk:size-5 sk:items-center sk:justify-center sk:bg-fill-tertiary">
        <img
          alt={providerKey}
          src={
            carrierServices?.[providerKey]?.icon ||
            '/images/carrier/fallback.svg'
          }
          className="sk:size-5"
        />
      </div>
      {carrierServices?.[providerKey]?.text ||
        CommonUtility.toCapitalize(providerKey || 'Unknown Carrier')}
    </div>
  );
}

export function FPOptionRateRenderer({
  packageItem,
  ownAccountLast4,
  carrierName = '',
  currentOrg,
}: {
  packageItem: ProviderRatePackage & {
    length?: number;
    width?: number;
    height?: number;
    pounds?: number;
    ounces?: number;
  };
  /** Set when the workspace's own carrier account quoted this rate. */
  ownAccountLast4?: string;
  carrierName?: string;
  currentOrg?: { company_name?: string; logo?: { url?: string } };
}) {
  return (
    <div className="sk:pl-2.5 sk:text-xs">
      <div className="sk:flex sk:items-center sk:justify-between sk:gap-2">
        <span className="sk:inline-flex sk:items-center sk:gap-2">
          {packageItem?.service?.key}
          {ownAccountLast4 ? (
            <OwnCarrierAccountMark
              carrierName={carrierName}
              last4={ownAccountLast4}
              workspaceName={currentOrg?.company_name}
              workspaceLogo={currentOrg?.logo?.url}
            />
          ) : null}
        </span>
        <div className="sk:flex sk:items-center sk:gap-1 sk:whitespace-nowrap">
          {packageItem?.days}
          <MuteText>Business days</MuteText>
        </div>
      </div>
      <div>
        <div className="sk:mt-1 sk:flex sk:items-center sk:justify-between sk:gap-2">
          <Badge className={MINI_PILL}>
            <span className="sk:inline-flex sk:items-center">
              {packageItem?.length?.toFixed(2)} x{' '}
              {packageItem?.width?.toFixed(2)} x{' '}
              {packageItem.height?.toFixed(2)}&quot;{' '}
              {(
                (packageItem?.pounds || 0) +
                (packageItem?.ounces || 0) / 16
              ).toFixed(2)}
            </span>
            <MuteText>lbs</MuteText>
          </Badge>
          <div className="sk:flex sk:items-center sk:gap-2">
            {packageItem?.credentials_source === 'platform' && (
              <Badge tone="success" className={MINI_PILL}>
                <MuteText>Source</MuteText>
                <img src="/favicon.svg" alt="" className="sk:size-3" />
              </Badge>
            )}
            <span className="sk:text-link">
              {CommonUtility.currencyFormat(packageItem?.rate || '-')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
